import { NavLink, useLocation } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { logout as logoutAction } from '../../store/slices/authSlice';
import { useLogoutMutation } from '../../store/slices/apiSlice';
import { useIsDeliveryUser } from '../../hooks/useUserRole';
import { DeliverySidebar } from './DeliverySidebar';
import { NAV_TOP_ITEMS, NAV_GROUPS } from '../../navigation/navCatalog';
import { isPathAllowedForUser } from '../../navigation/pageAccess';
import { LogOut, X, ChevronDown, ChevronRight } from 'lucide-react';

interface AppSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showCollapseButton?: boolean;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  collapsed = false,
  onToggleCollapse,
  showCollapseButton = false,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const isDeliveryUser = useIsDeliveryUser();
  const [triggerLogout, { isLoading: isLoggingOut }] = useLogoutMutation();

  const navTop = useMemo(
    () => NAV_TOP_ITEMS.filter((item) => isPathAllowedForUser(item.path, user)),
    [user]
  );

  const navGroups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => isPathAllowedForUser(item.path, user)),
      })).filter((group) => group.items.length > 0),
    [user]
  );

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    NAV_GROUPS.forEach((g) => {
      initial[g.label] = true;
    });
    return initial;
  });

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }));
  };

  const isItemActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isGroupActive = (groupItems: { path: string }[]) =>
    groupItems.some((item) => isItemActive(item.path));

  const onLogout = async () => {
    try {
      await triggerLogout({ refresh: '' }).unwrap().catch(() => undefined);
    } finally {
      dispatch(logoutAction());
      window.location.href = '/login';
    }
  };

  if (isDeliveryUser) {
    return (
      <DeliverySidebar
        isMobileMenuOpen={showCollapseButton}
        onToggleMobileMenu={onToggleCollapse}
      />
    );
  }

  return (
    <div className="bg-white border-r border-gray-200 h-full flex flex-col">
      {showCollapseButton && (
        <div className="p-3 border-b border-gray-200 flex justify-end">
          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
        {navTop.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={`
              group flex items-center px-2.5 py-2 text-sm font-medium rounded-md transition-all duration-200
              ${
                isItemActive(item.path)
                  ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }
            `}
          >
            <item.icon
              className={`w-4 h-4 mr-2.5 flex-shrink-0 ${collapsed ? 'mr-0' : ''}`}
            />
            {!collapsed && <span className="truncate">{item.label}</span>}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                {item.label}
              </div>
            )}
          </NavLink>
        ))}

        {!collapsed &&
          navGroups.map((group) => (
            <div key={group.label} className="mt-4">
              <button
                onClick={() => toggleGroup(group.label)}
                className={`
                w-full flex items-center justify-between px-2.5 py-2 text-xs font-semibold uppercase tracking-wide transition-colors rounded-md
                ${
                  isGroupActive(group.items)
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }
              `}
              >
                <span>{group.label}</span>
                {expandedGroups[group.label] ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>

              {expandedGroups[group.label] && (
                <div className="mt-1 space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={`
                      group flex items-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200
                      ${
                        isItemActive(item.path)
                          ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }
                    `}
                    >
                      <item.icon className="w-3 h-3 mr-2.5 flex-shrink-0" />
                      <span className="truncate text-xs">{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}

        {collapsed &&
          navGroups.map((group) =>
            group.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={`
                group flex items-center px-2.5 py-2 text-sm font-medium rounded-md transition-all duration-200
                ${
                  isItemActive(item.path)
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }
              `}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                  {item.label}
                </div>
              </NavLink>
            ))
          )}
      </nav>

      <div className="p-2 border-t border-gray-200">
        <button
          onClick={onLogout}
          disabled={isLoggingOut}
          className="w-full flex items-center justify-center sm:justify-start px-2.5 py-2 text-sm font-medium rounded-md transition-all duration-200 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogOut className="w-4 h-4 mr-2.5" />
          <span className="text-sm">{isLoggingOut ? 'Logging out...' : 'Log out'}</span>
        </button>
      </div>
    </div>
  );
};
